import ChartWrapper from '@components/charts/chart-wrapper/chart-wrapper.tsx';
import BarChart from '@components/charts/bar-chart/bar-chart.tsx';
import LineChart from '@components/charts/line-chart/line-chart.tsx';
import AreaChart from '@components/charts/area-chart/area-chart.tsx';
import PieChart from '@components/charts/pie-chart/pie-chart.tsx';
import MultiLineChart from '@components/charts/multi-line-chart/multi-line-chart.tsx';
import StackedBarChart from '@components/charts/stacked-bar-chart/stacked-bar-chart.tsx';
import LegendBarChart from '@components/charts/legend-bar-chart/legend-bar-chart.tsx';
import { PieChartDataItem } from '@shared/models/charts/pie-chart.model.ts';
import { LegendBarChartDataItem } from '@shared/models/charts/legend-bar-chart.model.ts';

type Point = {
  label: string;
  value: number;
  clients: number;
  devices: number;
};

const series: Point[] = [
  { label: 'Mon', value: 12, clients: 12, devices: 8 },
  { label: 'Tue', value: 19, clients: 19, devices: 11 },
  { label: 'Wed', value: 9, clients: 9, devices: 6 },
  { label: 'Thu', value: 22, clients: 22, devices: 14 },
  { label: 'Fri', value: 17, clients: 17, devices: 10 },
  { label: 'Sat', value: 25, clients: 25, devices: 18 },
  { label: 'Sun', value: 14, clients: 14, devices: 9 }
];

// value = percentage of the slice, count = raw count (shown above the percent).
const pieData: PieChartDataItem[] = [
  { name: 'Rooms', value: 41.27, count: 26, color: 'var(--blue-9)' },
  { name: 'Room Service', value: 30.16, count: 19, color: 'var(--teal)' },
  { name: 'Laundry', value: 15.87, count: 10, color: 'var(--orange-2)' },
  { name: 'Spa', value: 12.7, count: 8, color: 'var(--purple-6)' }
];

const legendData: LegendBarChartDataItem[] = [
  { id: 1, name: 'Reception', value: 40, color: 'var(--pink-1)' },
  { id: 2, name: 'Kitchen', value: 32, color: 'var(--blue-9)' },
  { id: 3, name: 'Spa', value: 21, color: 'var(--purple-6)' },
  { id: 4, name: 'Bar', value: 15, color: 'var(--orange-2)' }
];

function ChartsComponent() {
  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-xl font-bold text-gray-800">Charts</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartWrapper title="Bar Chart" description="Single-series bars">
          <BarChart<Point>
            data={series}
            config={{ xAxisKey: 'label', dataKey: 'value' }}
          />
        </ChartWrapper>

        <ChartWrapper title="Line Chart" description="Single-series line">
          <LineChart<Point>
            data={series}
            config={{ xAxisKey: 'label', dataKey: 'value', curveType: 'monotone' }}
          />
        </ChartWrapper>

        <ChartWrapper title="Area Chart" description="Filled area">
          <AreaChart<Point>
            data={series}
            config={{ xAxisKey: 'label', dataKey: 'value' }}
          />
        </ChartWrapper>

        <ChartWrapper title="Pie Chart" description="Distribution">
          <PieChart
            data={pieData}
            config={{
              height: 300,
              outerRadius: 110,
              showCount: true,
              minLabelPercent: 0.05,
              valueFormatter: (value) => `${value}%`
            }}
          />
        </ChartWrapper>

        <ChartWrapper title="Multi-line Chart" description="Two series">
          <MultiLineChart<Point>
            data={series}
            config={{
              xAxisKey: 'label',
              lines: [
                { dataKey: 'clients', color: 'var(--teal)', label: 'Clients' },
                { dataKey: 'devices', color: 'var(--purple-4)', label: 'Devices' }
              ]
            }}
          />
        </ChartWrapper>

        <ChartWrapper title="Stacked Bar Chart" description="Stacked layers">
          <StackedBarChart<Point>
            data={series}
            config={{
              xAxisKey: 'label',
              layers: [
                { dataKey: 'clients', color: 'var(--teal)', label: 'Clients' },
                { dataKey: 'devices', color: 'var(--purple-4)', label: 'Devices' }
              ]
            }}
          />
        </ChartWrapper>

        <ChartWrapper title="Legend Bar Chart" description="Colored bars + legend">
          <LegendBarChart data={legendData} config={{}} />
        </ChartWrapper>

        <ChartWrapper title="Loading state" description="ChartLoader">
          <BarChart<Point>
            data={[]}
            loading
            config={{ xAxisKey: 'label', dataKey: 'value' }}
          />
        </ChartWrapper>

        <ChartWrapper title="Empty state" description="ChartEmpty">
          <BarChart<Point>
            data={[]}
            config={{ xAxisKey: 'label', dataKey: 'value' }}
          />
        </ChartWrapper>
      </div>
    </div>
  );
}

export default ChartsComponent;
